import React, { useMemo, useState } from "react";
import { Crown, Mail, UserMinus, Users } from "lucide-react";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { useAuth } from "@/hooks/useAuth.hook";
import {
  useInviteStartupMember,
  useRemoveStartupMember,
  useStartupMembers,
} from "@/hooks/useTeamSharing";
import type { StartupMember, StartupTeamSettings } from "@/types/startup.types";

export interface TeamSharingPanelProps {
  startupId: string;
  isOwner: boolean;
  ownerId?: string;
  teamSettings?: Partial<StartupTeamSettings>;
  embedded?: boolean;
}

function formatRole(role: string): string {
  if (role === "owner") return "Owner";
  if (role === "editor") return "Editor";
  if (role === "viewer") return "Viewer";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function MemberRow({
  member,
  isOwner,
  isStartupOwner,
  onRemove,
  isRemoving,
}: {
  member: StartupMember;
  isOwner: boolean;
  isStartupOwner: boolean;
  onRemove?: (userId: string) => void;
  isRemoving: boolean;
}) {
  const showRemove =
    isOwner && !isStartupOwner && member.role !== "owner" && onRemove;

  return (
    <div className="flex items-center justify-between rounded-[14px] border border-white/10 bg-surface-elevated px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-white">
            {member.displayName || member.email}
          </span>
          {isStartupOwner ? (
            <Crown className="h-3.5 w-3.5 shrink-0 text-amber-400" aria-hidden />
          ) : null}
        </div>
        <div className="mt-0.5 truncate text-xs text-white/50">{member.email}</div>
      </div>
      <div className="ml-3 flex items-center gap-2">
        <span className="rounded bg-surface/10 px-2 py-0.5 text-xs text-white/60">
          {formatRole(member.role)}
        </span>
        {showRemove ? (
          <button
            type="button"
            onClick={() => onRemove(member.userId)}
            disabled={isRemoving}
            className="rounded-lg p-1.5 text-white/45 transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
            aria-label={`Remove ${member.displayName || member.email}`}
            title="Remove member"
          >
            <UserMinus className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export const TeamSharingPanel: React.FC<TeamSharingPanelProps> = ({
  startupId,
  isOwner,
  ownerId: ownerIdProp,
  teamSettings: teamSettingsProp,
  embedded = false,
}) => {
  const { firebaseUser } = useAuth();
  const [email, setEmail] = useState("");
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, isLoading, error } = useStartupMembers(startupId);
  const inviteMutation = useInviteStartupMember(startupId);
  const removeMutation = useRemoveStartupMember(startupId);

  const userId = firebaseUser?.uid;
  const resolvedOwnerId = data?.ownerId ?? ownerIdProp;
  const canManageTeam = useMemo(() => {
    if (isOwner) return true;
    if (!userId || !resolvedOwnerId) return false;
    return resolvedOwnerId === userId;
  }, [isOwner, userId, resolvedOwnerId]);

  const teamSettings = data?.teamSettings ?? teamSettingsProp;
  const allowInvites = teamSettings?.allowInvites !== false;
  const members = data?.members ?? [];
  const pendingInvites = data?.pendingInvites ?? [];
  const ownerId = resolvedOwnerId;

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    setFeedback(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFeedback({ type: "error", message: "Enter an email address to invite." });
      return;
    }

    try {
      const result = await inviteMutation.mutateAsync({ email: trimmedEmail });
      setEmail("");
      setFeedback({
        type: "success",
        message:
          result.status === "pending"
            ? `Invite sent to ${trimmedEmail}. They'll get access after accepting.`
            : `${trimmedEmail} now has access to this burn chart.`,
      });
    } catch (inviteError) {
      const message =
        inviteError instanceof Error
          ? inviteError.message.replace(/^HTTP error! status: \d+$/, "Could not send invite.")
          : "Could not send invite.";
      setFeedback({ type: "error", message });
    }
  };

  const handleRemove = async (memberUserId: string) => {
    if (
      !window.confirm("Remove this collaborator? They will lose access to this burn chart.")
    ) {
      return;
    }

    setFeedback(null);
    try {
      await removeMutation.mutateAsync(memberUserId);
      setFeedback({ type: "success", message: "Collaborator removed." });
    } catch (removeError) {
      const message =
        removeError instanceof Error ? removeError.message : "Could not remove member.";
      setFeedback({ type: "error", message });
    }
  };

  return (
    <div className={embedded ? "space-y-4" : "space-y-4 border-t border-white/10 pt-6"}>
      {!embedded ? (
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-brand" />
          <h3 className="text-lg font-semibold text-white">Share & Collaborate</h3>
        </div>
      ) : null}

      {!embedded ? (
        <p className="text-sm text-white/60">
          Invite other Burn Chart account holders to view and edit this burn chart
          together.
        </p>
      ) : null}

      {isLoading ? (
        <div className="text-sm text-white/50">Loading team...</div>
      ) : null}

      {error ? (
        <div className="rounded-[14px] border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
          Could not load team members. You can still send invites below if you are
          the owner.
        </div>
      ) : null}

      {data ? (
        <>
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-white/45">
              People with access ({members.length})
            </div>
            {members.length > 0 ? (
              members.map((member) => (
                <MemberRow
                  key={member.userId}
                  member={member}
                  isOwner={canManageTeam}
                  isStartupOwner={member.userId === ownerId || member.role === "owner"}
                  onRemove={handleRemove}
                  isRemoving={removeMutation.isPending}
                />
              ))
            ) : (
              <div className="rounded-[14px] border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white/50">
                No collaborators yet.
              </div>
            )}
          </div>

          {pendingInvites.length > 0 ? (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-white/45">
                Pending invites ({pendingInvites.length})
              </div>
              {pendingInvites.map((invite) => (
                <div
                  key={invite.userId}
                  className="flex items-center justify-between rounded-[14px] border border-amber-400/20 bg-amber-400/5 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-white">
                      {invite.displayName || invite.email}
                    </div>
                    <div className="truncate text-xs text-white/50">{invite.email}</div>
                  </div>
                  <span className="ml-3 rounded bg-amber-400/10 px-2 py-0.5 text-xs text-amber-300">
                    Awaiting acceptance
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {canManageTeam ? (
        allowInvites ? (
          <form onSubmit={handleInvite} className="space-y-3">
            <FormField label="Invite by email">
              <div className="flex gap-2">
                <div className="min-w-0 flex-1">
                  <Input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="colleague@company.com"
                    disabled={inviteMutation.isPending}
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={inviteMutation.isPending}
                  className="shrink-0"
                >
                  <Mail className="h-4 w-4" />
                  Invite
                </Button>
              </div>
            </FormField>
            {teamSettings?.requireApproval ? (
              <p className="text-xs text-white/45">
                New members must accept the invite before they can access this chart.
              </p>
            ) : (
              <p className="text-xs text-white/45">
                The person must already have a Burn Chart account with this email
                address.
              </p>
            )}
          </form>
        ) : (
          <div className="rounded-[14px] border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white/60">
            Team invites are turned off in team settings above.
          </div>
        )
      ) : (
        <div className="rounded-[14px] border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white/60">
          Only the burn chart owner can invite collaborators.
        </div>
      )}

      {feedback ? (
        <div
          className={`rounded-[14px] px-3 py-2 text-sm ${
            feedback.type === "success"
              ? "border border-brand/30 bg-brand/10 text-brand"
              : "border border-danger/30 bg-danger/10 text-danger"
          }`}
        >
          {feedback.message}
        </div>
      ) : null}
    </div>
  );
};
